import React, { useRef, useEffect } from 'react';
import * as THREE from 'three';
import styled from '@emotion/styled';

const ParticleContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;
  background: #000;
`;

const AnimatedBackground = () => {
  const containerRef = useRef(null);
  
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    // Scene setup
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    camera.position.z = 20;
    
    // Renderer setup
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(renderer.domElement);
    
    // Create circle texture for particles
    const createCircleTexture = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 64;
      canvas.height = 64;
      
      const context = canvas.getContext('2d');
      context.beginPath();
      context.arc(32, 32, 30, 0, Math.PI * 2);
      context.closePath();
      
      // Create radial gradient for softer circles
      const gradient = context.createRadialGradient(32, 32, 0, 32, 32, 32);
      gradient.addColorStop(0, 'rgba(255, 255, 255, 1)');
      gradient.addColorStop(0.5, 'rgba(255, 255, 255, 0.5)');
      gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
      
      context.fillStyle = gradient;
      context.fill();
      
      return new THREE.CanvasTexture(canvas);
    };
    
    const circleTexture = createCircleTexture();
    
    // Create particle system
    const particleCount = 10000; // Increased particle count
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);
    const sizes = new Float32Array(particleCount);
    const defaultSizes = new Float32Array(particleCount);
    const colorPalette = [
      new THREE.Color('#1a1a1a'),
      new THREE.Color('#222222'),
      new THREE.Color('#333333'),
      new THREE.Color('#444444'),
      new THREE.Color('#555555'),
      new THREE.Color('#666666'),
      new THREE.Color('#777777'),
      new THREE.Color('#888888')
    ];
    
    // Create geometry
    const geometry = new THREE.BufferGeometry();
    
    // Generate particles in a galaxy spiral shape
    for (let i = 0; i < particleCount; i++) {
      let x, y, z;
      
      // 70% of particles will form spiral arms, 30% will be random
      if (Math.random() < 0.7) {
        // Different number of arms with varied properties
        const numArms = 5;
        const armIndex = Math.floor(Math.random() * numArms);
        
        // Create unique offset for each arm to avoid regular spacing
        let armOffsets = [0, 2.1, 4.3, 5.9, 1.5]; // Irregular spacing
        const armOffset = armOffsets[armIndex];
        
        // Varied radius distribution for each arm
        const radiusScale = [2.0, 0.9, 1.1, 0.85, 1.15][armIndex];
        const radius = Math.pow(Math.random(), 0.7 + armIndex * 0.05) * 25 * radiusScale + 1;
        
        // Different spiral formula parameters for each arm
        // Some arms tighter, some looser, some with different curvature
        const armParams = [
          { a: 0.15, b: 0.25, formula: 'logarithmic' },    // Standard logarithmic spiral
          { a: 0.18, b: 0.18, formula: 'logarithmic' },    // Tighter spiral
          { a: 0.12, b: 0.32, formula: 'logarithmic' },    // Looser spiral
          { a: 0.5, p: 3.0, formula: 'archimedes' },       // Archimedes spiral
          { a: 0.25, pow: 0.6, formula: 'hyperbolic' }     // Hyperbolic spiral
        ][armIndex];
        
        let angle;
        
        // Apply different spiral formulas for different arms
        if (armParams.formula === 'logarithmic') {
          // Logarithmic spiral: r = a*e^(b*θ)
          angle = (Math.log(radius / armParams.a) / armParams.b) + armOffset;
        } 
        else if (armParams.formula === 'archimedes') {
          // Archimedes spiral: r = a + b*θ
          angle = (radius / armParams.a) + armOffset;
        }
        else if (armParams.formula === 'hyperbolic') {
          // Hyperbolic spiral: r = a/(θ^power)
          angle = Math.pow(armParams.a / radius, 1/armParams.pow) + armOffset;
        }
        
        // Add some waviness to certain arms
        if (armIndex % 2 === 0) {
          const waveFactor = 0.2 + (armIndex * 0.05);
          angle += Math.sin(radius * 0.4) * waveFactor;
        }
        
        // Some arms have more angle variance than others
        const angleVarianceScale = [1.0, 0.7, 1.2, 0.5, 1.1][armIndex];
        const angleVariance = (Math.random() * 0.1 - 0.05) * (radius / 25) * angleVarianceScale;
        angle += angleVariance;
        
        // Calculate position based on spiral formula
        x = radius * Math.cos(angle);
        y = radius * Math.sin(angle);
        
        // Varied thickness for different arms
        const armThicknessScale = [1.0, 0.8, 1.3, 0.9, 1.2][armIndex];
        const waveFreq = 4 + armIndex * 1.5; // Different wave pattern per arm
        const waveAmp = 0.15 + (armIndex * 0.03);
        const armThickness = (0.2 + (1 - radius / 25) * 1.0 + Math.sin(angle * waveFreq) * waveAmp) * armThicknessScale;
        
        // Some arms thicker in z, some flatter
        z = (Math.random() - 0.5) * armThickness;
        
        // Varied noise for different arms
        const noiseScale = [1.0, 0.7, 1.2, 0.8, 1.1][armIndex];
        const noiseFactor = (0.2 + (radius / 25) * 0.8) * noiseScale;
        const edgeFactor = Math.abs(z) / (armThickness / 2); 
        // Less noise along arm center, more at edges
        const totalNoise = noiseFactor * (0.2 + edgeFactor * 0.8);
        x += (Math.random() - 0.5) * totalNoise;
        y += (Math.random() - 0.5) * totalNoise;
        
        // Create denser clusters in arms with varied distribution
        if (Math.random() < 0.3 - (armIndex * 0.02)) {
          // Some arms have more/fewer clusters
          const clusterScale = [1.0, 0.7, 1.2, 0.9, 0.8][armIndex];
          const clusterRadius = (0.2 + Math.random() * 0.5) * clusterScale;
          x += (Math.random() - 0.5) * clusterRadius;
          y += (Math.random() - 0.5) * clusterRadius;
          defaultSizes[i] = 0.25 + Math.random() * 0.15; // Larger particles in clusters
        } else {
          defaultSizes[i] = 0.12 + (radius / 25) * 0.1 + Math.random() * 0.05;
        }
      } else {
        // Create random particles throughout the scene with various distributions
        
        // 70% in a wide halo
        if (Math.random() < 0.7) {
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.acos(2 * Math.random() - 1);
          const radius = 10 + Math.random() * 40; // Wider halo
          
          x = radius * Math.sin(phi) * Math.cos(theta);
          y = radius * Math.sin(phi) * Math.sin(theta);
          z = radius * Math.cos(phi) * 0.3; // Flattened in z-axis
          
          defaultSizes[i] = 0.05 + Math.random() * 0.05;
        } 
        // 30% in a disc but not following arms
        else {
          const theta = Math.random() * Math.PI * 2;
          const radius = Math.sqrt(Math.random()) * 20;
          
          x = radius * Math.cos(theta);
          y = radius * Math.sin(theta);
          z = (Math.random() - 0.5) * 2;
          
          defaultSizes[i] = 0.08 + Math.random() * 0.07;
        }
      }
      
      positions[i * 3] = x;
      positions[i * 3 + 1] = y;
      positions[i * 3 + 2] = z;
      
      // Enhanced color contrast for spiral arms
      const dist = Math.sqrt(x * x + y * y + z * z);
      
      // Lower color variance for more cohesive arms
      const colorVariance = Math.random() * 0.15;
      const colorIndex = Math.min(Math.floor((dist / 4) + colorVariance), colorPalette.length - 1);
      const color = colorPalette[colorIndex];
      
      // Increase brightness of spiral arm particles
      if (Math.abs(z) < 1.0 && dist < 22) { // Particles in the main disc plane and within galaxy radius
        // Brighter at core and along arm centers
        const brightnessFactor = 1.3 + (1 - dist / 25) * 0.7;
        colors[i * 3] = Math.min(color.r * brightnessFactor, 1.0);
        colors[i * 3 + 1] = Math.min(color.g * brightnessFactor, 1.0);
        colors[i * 3 + 2] = Math.min(color.b * brightnessFactor, 1.0);
      } 
      // Add some bright spots randomly
      else if (Math.random() < 0.05) {
        const brightness = 1.2 + Math.random() * 0.4;
        colors[i * 3] = Math.min(color.r * brightness, 1.0);
        colors[i * 3 + 1] = Math.min(color.g * brightness, 1.0);
        colors[i * 3 + 2] = Math.min(color.b * brightness, 1.0);
      } else {
        colors[i * 3] = color.r;
        colors[i * 3 + 1] = color.g;
        colors[i * 3 + 2] = color.b;
      }
      
      // Set particle size
      sizes[i] = defaultSizes[i];
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
    
    // Material with additive blending for glowing effect
    const material = new THREE.PointsMaterial({
      size: 0.2,
      vertexColors: true,
      transparent: true,
      opacity: 0.4, // Dimmed so white text stays readable
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      map: circleTexture,
      sizeAttenuation: true
    });
    
    // Create the particle system
    const particleSystem = new THREE.Points(geometry, material);
    scene.add(particleSystem);
    
    // Create cursor star effect
    const createCursorStar = () => {
      // Create proper star shape with points
      const starGeometry = new THREE.BufferGeometry();
      const starVertices = [];
      
      // Create a proper star shape with 5 points
      const numPoints = 5;
      const outerRadius = 0.35; // Even smaller outer radius
      const innerRadius = 0.15; // Even smaller inner radius
      
      // Create star shape vertices
      for (let i = 0; i < numPoints * 2; i++) {
        // Alternate between outer and inner points
        const radius = i % 2 === 0 ? outerRadius : innerRadius;
        const angle = (i / (numPoints * 2)) * Math.PI * 2;
        
        // Add vertex at this angle and radius
        starVertices.push(
          radius * Math.cos(angle),
          radius * Math.sin(angle),
          0
        );
      }
      
      // Connect vertices to form a star shape using triangles
      const indices = [];
      for (let i = 0; i < numPoints * 2; i++) {
        indices.push(i, (i + 1) % (numPoints * 2), numPoints * 2);
      }
      
      // Add center point
      starVertices.push(0, 0, 0);
      
      starGeometry.setAttribute(
        'position', 
        new THREE.Float32BufferAttribute(starVertices, 3)
      );
      starGeometry.setIndex(indices);
      
      // Create line geometry for star outline
      const lineGeometry = new THREE.BufferGeometry();
      const lineVertices = [];
      
      // Copy the star outline vertices but exclude center point
      for (let i = 0; i < numPoints * 2; i++) {
        const vIndex = i * 3;
        lineVertices.push(
          starVertices[vIndex],
          starVertices[vIndex + 1],
          starVertices[vIndex + 2]
        );
      }
      // Close the shape by adding the first point again
      lineVertices.push(
        starVertices[0],
        starVertices[1],
        starVertices[2]
      );
      
      lineGeometry.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(lineVertices, 3)
      );
      
      // Star mesh with same color as particles but brighter
      const starMaterial = new THREE.MeshBasicMaterial({
        color: 0x888888, // Similar to particle color
        transparent: true,
        opacity: 0.9,
        side: THREE.DoubleSide
      });
      const starMesh = new THREE.Mesh(starGeometry, starMaterial);
      
      // Star outline with subtle glow effect
      const lineMaterial = new THREE.LineBasicMaterial({
        color: 0xaaaaaa,
        transparent: true,
        opacity: 0.7,
        blending: THREE.AdditiveBlending
      });
      const starOutline = new THREE.Line(lineGeometry, lineMaterial);
      
      // Create a group to hold all parts
      const starGroup = new THREE.Group();
      starGroup.add(starMesh);
      starGroup.add(starOutline);
      starGroup.visible = false;
      
      // Add to scene
      scene.add(starGroup);
      
      return starGroup;
    };
    
    const cursorStar = createCursorStar();
    
    // Mouse tracking
    let mouseX = 0;
    let mouseY = 0;
    let mouseWorldPosition = new THREE.Vector3();
    let isMouseOnScreen = false;
    
    // Add trail storage
    const trailLength = 10;
    const trailStars = [];
    const trailPositions = [];
    
    // Function to create a small star for the trail
    const createTrailStar = () => {
      // Create a simple star (smaller than cursor star)
      const starGeometry = new THREE.BufferGeometry();
      const starVertices = [];
      
      const numPoints = 5;
      const outerRadius = 0.2;  // Increased size
      const innerRadius = 0.08; // Increased size
      
      for (let i = 0; i < numPoints * 2; i++) {
        const radius = i % 2 === 0 ? outerRadius : innerRadius;
        const angle = (i / (numPoints * 2)) * Math.PI * 2;
        
        starVertices.push(
          radius * Math.cos(angle),
          radius * Math.sin(angle),
          0
        );
      }
      
      const indices = [];
      for (let i = 0; i < numPoints * 2; i++) {
        indices.push(i, (i + 1) % (numPoints * 2), numPoints * 2);
      }
      
      starVertices.push(0, 0, 0);
      
      starGeometry.setAttribute(
        'position', 
        new THREE.Float32BufferAttribute(starVertices, 3)
      );
      starGeometry.setIndex(indices);
      
      const starMaterial = new THREE.MeshBasicMaterial({
        color: 0xaaaaaa,  // Brighter color
        transparent: true,
        opacity: 0.9,     // Higher opacity
        side: THREE.DoubleSide
      });
      const starMesh = new THREE.Mesh(starGeometry, starMaterial);
      
      starMesh.visible = false;
      scene.add(starMesh);
      
      return starMesh;
    };
    
    // Initialize trail stars
    for (let i = 0; i < trailLength; i++) {
      const star = createTrailStar();
      trailStars.push(star);
      trailPositions.push(new THREE.Vector3());
    }
    
    const onDocumentMouseMove = (event) => {
      isMouseOnScreen = true;
      
      // Mouse coordinates for camera movement
      mouseX = (event.clientX - window.innerWidth / 2) / 100;
      mouseY = (event.clientY - window.innerHeight / 2) / 100;
      
      // Convert mouse position to world coordinates for particle interaction
      const mousePos = new THREE.Vector2(
        (event.clientX / window.innerWidth) * 2 - 1,
        -(event.clientY / window.innerHeight) * 2 + 1
      );
      
      mouseWorldPosition.set(mousePos.x * 20, mousePos.y * 10, 0);
      
      // Update cursor star position
      cursorStar.position.copy(mouseWorldPosition);
      cursorStar.visible = true;
      
      // Update trail positions - with shorter spacing between positions
      for (let i = trailLength - 1; i > 0; i--) {
        trailPositions[i].copy(trailPositions[i-1]);
      }
      trailPositions[0].copy(mouseWorldPosition);
      
      // Update trail stars visibility and position
      for (let i = 0; i < trailLength; i++) {
        trailStars[i].position.copy(trailPositions[i]);
        
        // Add slight offset based on position in trail (much smaller offset)
        // This creates a smaller trail that stays closer to the cursor
        const offset = 0.1 * i; // Reduced offset for closer trail
        trailStars[i].position.x += (Math.random() - 0.5) * offset * 0.2;
        trailStars[i].position.y += (Math.random() - 0.5) * offset * 0.2;
        
        trailStars[i].visible = true;
        // Fade opacity based on position in trail (higher minimum opacity)
        trailStars[i].material.opacity = 0.95 * (1 - i / trailLength * 0.7);
        // Decrease size based on position in trail (more gradual decrease)
        trailStars[i].scale.set(1 - (i / trailLength * 0.3), 1 - (i / trailLength * 0.3), 1);
      }
    };
    
    const onDocumentMouseLeave = () => {
      isMouseOnScreen = false;
      cursorStar.visible = false;
      
      // Hide trail stars
      for (let i = 0; i < trailLength; i++) {
        trailStars[i].visible = false;
      }
    };
    
    document.addEventListener('mousemove', onDocumentMouseMove, false);
    document.addEventListener('mouseleave', onDocumentMouseLeave, false);
    
    // Handle window resize
    const onWindowResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };
    
    window.addEventListener('resize', onWindowResize, false);
    
    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);
      
      // Rotate particle system - adjust rotation speed based on mouse position
      particleSystem.rotation.x += 0.0005 + Math.abs(mouseY) * 0.0005;
      particleSystem.rotation.y += 0.001 + Math.abs(mouseX) * 0.0005;
      
      // Smooth camera movement following mouse - increased reactivity
      camera.position.x += (mouseX - camera.position.x) * 0.03;
      camera.position.y += (-mouseY - camera.position.y) * 0.03;
      camera.lookAt(scene.position);
      
      // Animate cursor star
      if (isMouseOnScreen && cursorStar.visible) {
        // Remove pulsating and rotation animations
        cursorStar.scale.x = cursorStar.scale.y = cursorStar.scale.z = 1;
      }
      
      // Update particles based on mouse position
      const sizeAttribute = geometry.attributes.size;
      const colorAttribute = geometry.attributes.color;
      const positionAttribute = geometry.attributes.position;
      
      for (let i = 0; i < particleCount; i++) {
        const x = positionAttribute.getX(i);
        const y = positionAttribute.getY(i);
        const z = positionAttribute.getZ(i);
        
        // Calculate distance to mouse position in world space
        const particlePos = new THREE.Vector3(x, y, z);
        // Apply rotation to get accurate position
        particlePos.applyQuaternion(particleSystem.quaternion);
        
        const distance = particlePos.distanceTo(mouseWorldPosition);
        
        // Particles react to mouse proximity
        if (distance < 5) {
          // Increase size based on proximity
          const scaleFactor = 1 + (1 - distance / 5) * 2;
          sizeAttribute.setX(i, defaultSizes[i] * scaleFactor);
          
          // Brighten color
          const brightnessFactor = 1 + (1 - distance / 5) * 1.5;
          colorAttribute.setX(i, Math.min(colorAttribute.getX(i) * brightnessFactor, 1.0));
          colorAttribute.setY(i, Math.min(colorAttribute.getY(i) * brightnessFactor, 1.0));
          colorAttribute.setZ(i, Math.min(colorAttribute.getZ(i) * brightnessFactor, 1.0));
        } else {
          // Reset to default
          sizeAttribute.setX(i, defaultSizes[i]);
          
          // Reset colors gradually
          const r = colorAttribute.getX(i);
          const g = colorAttribute.getY(i);
          const b = colorAttribute.getZ(i);
          
          // Get original color
          const dist = Math.sqrt(x * x + y * y + z * z);
          const colorIndex = Math.min(Math.floor(dist / 4), colorPalette.length - 1);
          const targetColor = colorPalette[colorIndex];
          
          // Smoothly transition back to original color
          colorAttribute.setX(i, r + (targetColor.r - r) * 0.1);
          colorAttribute.setY(i, g + (targetColor.g - g) * 0.1);
          colorAttribute.setZ(i, b + (targetColor.b - b) * 0.1);
        }
      }
      
      colorAttribute.needsUpdate = true;
      sizeAttribute.needsUpdate = true;
      
      // Breathing effect
      const time = Date.now() * 0.0005;
      particleSystem.scale.x = particleSystem.scale.y = particleSystem.scale.z = 
        1 + Math.sin(time) * 0.05;
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Cleanup
    return () => {
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      
      window.removeEventListener('resize', onWindowResize);
      document.removeEventListener('mousemove', onDocumentMouseMove);
      document.removeEventListener('mouseleave', onDocumentMouseLeave);
      
      // Dispose all resources
      geometry.dispose();
      material.dispose();
      circleTexture.dispose();
      
      // Dispose trail stars
      for (let i = 0; i < trailLength; i++) {
        trailStars[i].geometry.dispose();
        trailStars[i].material.dispose();
      }
      
      renderer.dispose();
    };
  }, []);
  
  return <ParticleContainer ref={containerRef} />;
};

export default AnimatedBackground; 